import React, { useEffect, useState } from 'react';
import { AlertTriangle, ExternalLink, X } from 'lucide-react';
import { canonicalOrigin } from '../lib/canonicalOrigin';

/**
 * PwaRelocationBanner — einmaliger Umzugs-Hinweis für Kunden, deren PWA
 * versehentlich mit der Preview-Domain (`*.preview.emergentagent.com`)
 * installiert wurde.
 *
 * Zeigt einen prominenten Full-Bleed-Banner, wenn:
 *   1. die App im PWA-Standalone-Modus läuft,
 *   2. die aktuelle Host-Domain NICHT die kanonische ist,
 *   3. der User den Hinweis noch nicht permanent verworfen hat.
 *
 * Klick auf CTA öffnet fuelxcard.com im neuen Tab — dort kann der Kunde
 * die App frisch installieren. Der "X"-Button unterdrückt den Banner
 * für 24h (localStorage).
 */
const DISMISS_KEY = 'fuelx_pwa_relocation_dismissed_until';
const DISMISS_MS = 24 * 60 * 60 * 1000; // 24h

function isStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.matchMedia?.('(display-mode: standalone)').matches ||
      window.navigator.standalone === true
    );
  } catch { return false; }
}

function isOnPreviewDomain() {
  if (typeof window === 'undefined') return false;
  const host = window.location.host || '';
  return /preview\.emergentagent\.com$/.test(host) || /\.preview\./.test(host);
}

function isDismissed() {
  try {
    const until = parseInt(localStorage.getItem(DISMISS_KEY) || '0', 10);
    return until > Date.now();
  } catch { return false; }
}

export default function PwaRelocationBanner() {
  const [show, setShow] = useState(false);

  useEffect(() => {
    // Einmalig prüfen — nur wenn Standalone-PWA UND auf Preview-Domain
    if (isStandalone() && isOnPreviewDomain() && !isDismissed()) {
      setShow(true);
    }
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS)); } catch { /* ignore */ }
    setShow(false);
  };

  const goToCanonical = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS * 30)); } catch { /* ignore */ }
    // Öffnet die Produktions-Domain — Kunde kann dort neu installieren
    const target = `${canonicalOrigin()}/?relocated=1`;
    window.location.replace(target);
  };

  if (!show) return null;

  return (
    <div
      className="fixed inset-x-0 top-0 z-[100] bg-gradient-to-r from-amber-500 via-orange-500 to-rose-500 text-white shadow-xl"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
      data-testid="pwa-relocation-banner"
      role="alert"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-3 flex items-start sm:items-center gap-3">
        <AlertTriangle className="w-5 h-5 flex-shrink-0 mt-0.5 sm:mt-0" strokeWidth={2.5} />
        <div className="flex-1 min-w-0 text-sm leading-snug">
          <p className="font-medium">
            Deine App zeigt auf die Test-Umgebung.
          </p>
          <p className="text-white/90 text-xs mt-0.5">
            Bitte auf <span className="font-mono font-semibold">fuelxcard.com</span> neu installieren — dort läuft die stabile Version.
          </p>
        </div>
        <button
          onClick={goToCanonical}
          className="hidden sm:inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full bg-white text-stone-900 text-xs font-medium hover:bg-stone-100 transition-colors flex-shrink-0"
          data-testid="pwa-relocation-cta"
        >
          Jetzt öffnen <ExternalLink className="w-3 h-3" strokeWidth={2.5} />
        </button>
        <button
          onClick={dismiss}
          className="p-1.5 hover:bg-white/20 rounded-full flex-shrink-0"
          aria-label="Ausblenden"
          data-testid="pwa-relocation-dismiss"
        >
          <X className="w-4 h-4" strokeWidth={2.5} />
        </button>
      </div>
      {/* Mobile-CTA — vollbreiter Button darunter */}
      <button
        onClick={goToCanonical}
        className="sm:hidden w-full mt-1 px-4 py-2.5 bg-black/20 backdrop-blur-sm text-white text-xs font-medium flex items-center justify-center gap-1.5 hover:bg-black/30 transition-colors"
        data-testid="pwa-relocation-cta-mobile"
      >
        Auf fuelxcard.com öffnen <ExternalLink className="w-3.5 h-3.5" strokeWidth={2.5} />
      </button>
    </div>
  );
}
